import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Alert,
  Chip,
  Divider,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress
} from '@mui/material';
import {
  Save,
  Send,
  AutoAwesome,
  Warning,
  CheckCircle,
  Assignment
} from '@mui/icons-material';
import { formService } from '../services/formService';
import { useNotification } from '../contexts/NotificationContext';

const FormBuilder = () => {
  const { country, visaType } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning } = useNotification();
  
  const [formTemplate, setFormTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [completeness, setCompleteness] = useState(0);
  const [showPremiumDialog, setShowPremiumDialog] = useState(false);

  useEffect(() => {
    loadFormTemplate();
  }, [country, visaType]);

  const loadFormTemplate = async () => {
    try {
      setLoading(true);
      const response = await formService.getFormTemplate(country, visaType);
      setFormTemplate(response);
      
      // Initialize form data with empty values
      const initialData = {};
      response.formFields.forEach(field => {
        initialData[field.field] = getDefaultValue(field);
      });
      setFormData(initialData);
      
      calculateCompleteness(initialData, response.formFields);
    } catch (error) {
      console.error('Failed to load form template:', error);
      showError('Failed to load form template');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const getDefaultValue = (field) => {
    switch (field.type) {
      case 'checkbox':
        return false;
      case 'number':
        return '';
      case 'date':
        return '';
      case 'select':
        return '';
      default:
        return '';
    }
  };

  const calculateCompleteness = (data, fields) => {
    const requiredFields = fields.filter(f => f.required);
    const filledRequired = requiredFields.filter(f => {
      const value = data[f.field];
      return value !== '' && value !== null && value !== undefined && value !== false;
    });
    
    const percentage = requiredFields.length > 0 
      ? (filledRequired.length / requiredFields.length) * 100 
      : 100;
    
    setCompleteness(Math.round(percentage));
  };

  const handleInputChange = (fieldName, value) => {
    const newData = { ...formData, [fieldName]: value };
    setFormData(newData);
    calculateCompleteness(newData, formTemplate.formFields);
  };

  const handleAutoFill = async () => {
    try {
      setGenerating(true);
      const response = await formService.generateForm(country, visaType, true);
      
      setFormData(response.filledData);
      setWarnings(response.warnings || []);
      setCompleteness(response.completeness || 0);
      
      showSuccess('Form auto-filled successfully!');
    } catch (error) {
      if (error.response?.status === 403) {
        setShowPremiumDialog(true);
      } else {
        console.error('Failed to generate form:', error);
        showError('Failed to auto-fill form');
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      setSaving(true);
      await formService.saveApplication(country, visaType, formData);
      showSuccess('Draft saved successfully');
    } catch (error) {
      console.error('Failed to save draft:', error);
      showError('Failed to save draft');
    } finally {
      setSaving(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.field] || getDefaultValue(field);

    switch (field.type) {
      case 'text':
        return (
          <TextField
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleInputChange(field.field, e.target.value)}
            required={field.required}
            margin="normal"
          />
        );

      case 'date':
        return (
          <TextField
            fullWidth
            label={field.label}
            type="date"
            value={value}
            onChange={(e) => handleInputChange(field.field, e.target.value)}
            required={field.required}
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
        );

      case 'number':
        return (
          <TextField
            fullWidth
            label={field.label}
            type="number"
            value={value}
            onChange={(e) => handleInputChange(field.field, e.target.value)}
            required={field.required}
            margin="normal"
          />
        );

      case 'select':
        return (
          <FormControl fullWidth margin="normal" required={field.required}>
            <InputLabel>{field.label}</InputLabel>
            <Select
              value={value}
              label={field.label}
              onChange={(e) => handleInputChange(field.field, e.target.value)}
            >
              {field.options?.map((option) => (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );

      case 'checkbox':
        return (
          <FormControlLabel
            control={
              <Checkbox
                checked={value}
                onChange={(e) => handleInputChange(field.field, e.target.checked)}
              />
            }
            label={field.label}
            sx={{ mt: 2 }}
          />
        );

      case 'object':
        return (
          <TextField
            fullWidth
            label={field.label}
            value={typeof value === 'object' ? JSON.stringify(value) : value}
            onChange={(e) => {
              try {
                const parsed = JSON.parse(e.target.value);
                handleInputChange(field.field, parsed);
              } catch {
                handleInputChange(field.field, e.target.value);
              }
            }}
            required={field.required}
            margin="normal"
            multiline
            rows={3}
            helperText="Enter as JSON object or text"
          />
        );

      default:
        return (
          <TextField
            fullWidth
            label={field.label}
            value={value}
            onChange={(e) => handleInputChange(field.field, e.target.value)}
            required={field.required}
            margin="normal"
          />
        );
    }
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (!formTemplate) {
    return (
      <Container maxWidth="md">
        <Alert severity="error">
          Form template not found for {country} - {visaType}
        </Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, mb: 1 }}>
            {country} - {visaType} Form
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Complete your immigration application form
          </Typography>
        </Box>

        {/* Progress and Actions */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Form Completion</Typography>
              <Chip 
                label={`${completeness}% Complete`}
                color={completeness === 100 ? 'success' : 'primary'}
                icon={completeness === 100 ? <CheckCircle /> : <Assignment />}
              />
            </Box>
            
            <LinearProgress 
              variant="determinate" 
              value={completeness} 
              sx={{ mb: 3, height: 8, borderRadius: 4 }}
            />

            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={generating ? <CircularProgress size={20} /> : <AutoAwesome />}
                onClick={handleAutoFill}
                disabled={generating}
              >
                {generating ? 'Generating...' : 'Auto-Fill (Premium)'}
              </Button>
              
              <Button
                variant="outlined"
                startIcon={saving ? <CircularProgress size={20} /> : <Save />}
                onClick={handleSaveDraft}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Draft'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Warnings */}
        {warnings.length > 0 && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Please review the following:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {warnings.map((warning, index) => (
                <li key={index}>
                  <Typography variant="body2">{warning}</Typography>
                </li>
              ))}
            </ul>
          </Alert>
        )}

        {/* Disclaimer */}
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            {formTemplate.disclaimer}
          </Typography>
        </Alert>

        {/* Form Fields */}
        <Card>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 3 }}>
              Application Form
            </Typography>

            {formTemplate.formFields.map((field, index) => (
              <Box key={field.field}>
                {renderField(field)}
                {index < formTemplate.formFields.length - 1 && (
                  <Divider sx={{ my: 2 }} />
                )}
              </Box>
            ))}

            {/* Submit Actions */}
            <Box sx={{ mt: 4, pt: 3, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Form Actions
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  disabled={completeness < 100}
                  onClick={() => showWarning('Submission feature coming soon!')}
                >
                  Submit Application
                </Button>
                
                <Button
                  variant="outlined"
                  onClick={() => navigate('/dashboard')}
                >
                  Back to Dashboard
                </Button>
              </Box>

              {completeness < 100 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                  Complete all required fields to submit your application.
                </Typography>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Premium Dialog */}
        <Dialog 
          open={showPremiumDialog} 
          onClose={() => setShowPremiumDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AutoAwesome color="primary" />
            Premium Feature
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" paragraph>
              Auto-fill functionality is available with our Premium subscription.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Premium features include:
            </Typography>
            <ul>
              <li>AI-powered form auto-filling</li>
              <li>Advanced immigration matching</li>
              <li>Priority support</li>
              <li>Document templates</li>
            </ul>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowPremiumDialog(false)}>
              Maybe Later
            </Button>
            <Button variant="contained" onClick={() => setShowPremiumDialog(false)}>
              Upgrade to Premium
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default FormBuilder;